import { Grid } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { CustomCartAlert } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/CartAlerts/components/CustomCartAlert';
import { PaymentMethodsAlert } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/CartAlerts/components/PaymentMethodsAlert';
import { Section } from '@/components/modules/Application/components/_shared/components/Section/Section';
import { AutoDetailsFormWrapper } from '@/components/modules/Application/components/Cart/AutoCart/components/AutoDetailsFormWrapper';
import { useAutoDetails } from '@/components/modules/Application/components/Cart/AutoCart/hooks/useAutoDetails';
import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { ItemMap } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/maps/ItemMap';
import { CartItemDialogFormSchema } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/schemas/CartItemDialogFormSchema';
import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import {
  DialogStepContent,
  DialogStepProps,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/components/DialogStepContent';
import { useCheckoutSessionCart } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useCheckoutSessionCart';
import { areItemsEqual } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/EditCheckoutSessionCartDialog/components/steps/CartEdit/utils/areItemsEqual';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FormWrapper } from '@/components/shared/components/FormWrapper';
import { Text } from '@/components/shared/components/Text';

export const AutoCartEdit = (props: DialogStepProps) => {
  const { cart } = useCart();

  const initialFormItem = cart?.items[0]?.toFormItem();

  if (!initialFormItem) {
    return null;
  }

  return (
    <FormWrapper
      formSchema={CartItemDialogFormSchema}
      defaultValues={{
        item: initialFormItem,
      }}
    >
      <AutoCartEditForm {...props} />
    </FormWrapper>
  );
};

export const AutoCartEditForm = ({ onNext, onPrev }: DialogStepProps) => {
  const { watch, trigger, handleSubmit } = useFormContext();
  const { checkoutSession } = useCheckoutSessionCart();
  const { cart, paymentMethodsAvailable } = useAutoDetails();
  const { arePaymentMethodsAvailable, totalToFinance } = usePaymentMethods();

  const [areAlertsVisible, setAreAlertsVisible] = useState<boolean>(false);

  const watchBrand = watch('item.brand');
  const watchModel = watch('item.model');

  useEffect(() => {
    trigger();
  }, [watchBrand, watchModel]);

  // Could be improved: dates can pose a problem because of timezones etc.
  const cartHasChanges = areItemsEqual(
    cart?.items,
    checkoutSession ? ItemMap.fromCheckoutSessionToItems(checkoutSession) : undefined,
  );

  const hasAnyAlert = !paymentMethodsAvailable || !cartHasChanges;

  const onSubmit = () => {
    if (hasAnyAlert) {
      showAlerts();
      return;
    }

    handleSubmit(onNext, showAlerts)();
  };

  const showAlerts = () => {
    if (!areAlertsVisible) setAreAlertsVisible(true);
  };

  return (
    <DialogStepContent
      titleTextId={'CART_EDIT_AUTO'}
      descriptionTextId={'CART_EDIT_INSTRUCTIONS_AUTO'}
      prevButtonTextId={'CLOSE'}
      onNext={onSubmit}
      onPrev={onPrev}
      disabled={areAlertsVisible && hasAnyAlert}
    >
      <Section>
        <AutoDetailsFormWrapper loading={false} />
        {areAlertsVisible && (
          <Grid container justifyContent={'flex-end'} spacing={2}>
            <Grid item xs={6}>
              <FlexColumn mt={2} alignItems={'stretch'}>
                {!paymentMethodsAvailable && <PaymentMethodsAlert {...arePaymentMethodsAvailable(totalToFinance)} />}
                {!cartHasChanges && (
                  <CustomCartAlert>
                    <Text tId={'ALERT_VEHICLE_NO_CHANGES'} />
                  </CustomCartAlert>
                )}
              </FlexColumn>
            </Grid>
          </Grid>
        )}
      </Section>
    </DialogStepContent>
  );
};
